/**
 * DataStore Module
 *
 * Provides a unified interface for data storage operations.
 * Supports both offline (IndexedDB) and authenticated (Supabase) modes.
 */

export * from './types';
export { OfflineDataStore, getOfflineDataStore } from './offline';
export { SupabaseDataStore, getSupabaseDataStore } from './supabase';

import type { IDataStore } from './types';
import { getOfflineDataStore } from './offline';
import { getSupabaseDataStore } from './supabase';

/**
 * Tracks whether the app is in authenticated mode.
 * This is set by the auth system when login/logout occurs.
 */
let isAuthenticatedMode = false;

/**
 * Set the authentication mode for datastore selection.
 * Call this when auth state changes.
 */
export function setAuthenticatedMode(authenticated: boolean): void {
  isAuthenticatedMode = authenticated;
}

/**
 * Check if we're currently in authenticated mode.
 */
export function getAuthenticatedMode(): boolean {
  return isAuthenticatedMode;
}

/**
 * Get the active datastore instance.
 *
 * Returns the Supabase datastore when authenticated,
 * otherwise returns the offline IndexedDB datastore.
 */
export function getDataStore(): IDataStore {
  if (isAuthenticatedMode) {
    return getSupabaseDataStore();
  }
  return getOfflineDataStore();
}

/**
 * Initialize the datastore.
 * Call this early in app startup.
 */
export async function initializeDataStore(): Promise<IDataStore> {
  const store = getDataStore();
  await store.initialize();
  return store;
}
